import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ClientCombobox from "@/components/ClientCombobox";
import ClientArea from "@/pages/ClientArea";
import { UserRound, Eye } from "lucide-react";

/**
 * 🆕 Feb 2026 — Portale Cliente (vista staff)
 * Riservata a admin/agente/backoffice: mostra ESATTAMENTE l'area riservata
 * che vede il cliente dopo il login, in sola lettura, popolata con i dati
 * reali del cliente selezionato.
 *
 * Meccanismo: imposta `sessionStorage.portal_view_client_id` così che
 * l'axios interceptor riscriva `/my/*` in `/admin/clients/{cid}/portal-view/*`.
 */
export default function Portal() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState("");
  const [renderKey, setRenderKey] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  }, []);

  // Ogni volta che cambia il cliente selezionato, imposto il flag e forzo il remount di ClientArea.
  // 🆕 Fix Feb 2026 — la key include un timestamp: anche se l'admin riseleziona LO
  // STESSO cliente, ClientArea si smonta+rimonta e rifà tutte le fetch. Nessun
  // caching lato React.
  useEffect(() => {
    if (selected) {
      sessionStorage.setItem("portal_view_client_id", selected);
    } else {
      sessionStorage.removeItem("portal_view_client_id");
    }
    setRenderKey(`${selected}-${Date.now()}`);
    return () => {
      // Pulizia quando smontiamo la pagina
      sessionStorage.removeItem("portal_view_client_id");
    };
  }, [selected]);

  const selectedClient = clients.find((c) => c.id === selected);

  return (
    <div className="animate-fade-up" data-testid="portal-page">
      <div className="max-w-3xl mx-auto px-3 sm:px-6 pt-6 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Eye className="h-4 w-4 text-blue-600" />
            <span>Visualizzazione in sola lettura dell&apos;area riservata cliente</span>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wide text-slate-500">
            <UserRound className="h-3.5 w-3.5" /> Cliente
          </div>
          <ClientCombobox
            clients={clients}
            value={selected}
            onChange={setSelected}
            placeholder="Seleziona un cliente per vedere la sua area riservata…"
            testid="portal-client-select"
          />
          {selectedClient && (
            <p className="mt-2 text-xs text-slate-500">
              Stai guardando l&apos;area di <strong>{selectedClient.surname} {selectedClient.name}</strong>.
              I dati sono reali; nessuna azione (richieste, messaggi, aggiornamenti) è disponibile in questa vista.
            </p>
          )}
        </div>
      </div>

      {selected ? (
        <ClientArea key={renderKey} readOnly hideAccountBar />
      ) : (
        <div className="max-w-3xl mx-auto px-3 sm:px-6">
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-16 text-center text-slate-400">
            Seleziona un cliente per visualizzare la sua area riservata.
          </div>
        </div>
      )}
    </div>
  );
}
